// OP-3619 (D7) — Contrato › Pedido › Remessa › Peça, no topo da ficha.
//
// A regra de qual cadeia mostrar mora em `cadeia.ts`; aqui só se desenha o que
// ela devolveu. Elo que não existe aparece apagado com o motivo, nunca em
// branco — foi a lição do D5: o que a tela não diz, ninguém adivinha.
import { FC, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { BD, CELULAR } from '../styles'
import { Cadeia, Elo } from './cadeia'
import { rotaDaReferencia } from './referencia'
import { Ancora } from './RefLink'

const Bloco = styled.div`
  margin-bottom: 16px;
  @media ${CELULAR} {
    margin: 0 -16px 16px;
  }
`

const Faixa = styled.nav`
  display: flex;
  align-items: stretch;
  gap: 4px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  @media ${CELULAR} {
    padding: 0 16px;
  }
`

const Depois = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${BD.grayText};
  @media ${CELULAR} {
    padding: 0 16px;
  }
`

const Caixa = styled.div<{ $atual?: boolean; $apagado?: boolean }>`
  flex-shrink: 0;
  min-width: 0;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${({ $atual }) => ($atual ? BD.red : BD.grayBorder)};
  background: ${({ $atual }) => ($atual ? BD.redLight : '#FFF')};
  opacity: ${({ $apagado }) => ($apagado ? 0.6 : 1)};
`

const Rotulo = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${BD.grayText};
`

const Codigo = styled.div<{ $atual?: boolean }>`
  font-size: 13px;
  font-weight: ${({ $atual }) => ($atual ? 700 : 500)};
  color: ${({ $atual }) => ($atual ? BD.redDark : BD.black)};
  white-space: nowrap;
`

const Motivo = styled.div`
  font-size: 12px;
  color: ${BD.grayText};
  white-space: nowrap;
`

const Link = styled.a`
  text-decoration: none;
  flex-shrink: 0;
`

const Seta = styled.span`
  align-self: center;
  flex-shrink: 0;
  color: ${BD.grayText};
`

const Conteudo: FC<{ elo: Elo; marcar?: boolean }> = ({ elo, marcar }) => (
  <Caixa $atual={elo.atual} $apagado={!!elo.ausente} aria-current={marcar && elo.atual ? 'step' : undefined}>
    <Rotulo>{elo.rotulo}</Rotulo>
    {elo.ausente ? <Motivo>{elo.ausente}</Motivo> : <Codigo $atual={elo.atual}>{elo.codigo ?? '—'}</Codigo>}
  </Caixa>
)

export const FaixaDaVida: FC<{ cadeia: Cadeia }> = ({ cadeia }) => {
  const navigate = useNavigate()
  if (cadeia.elos.length === 0) return null
  return (
    <Bloco>
      <Faixa aria-label="Cadeia da peça">
        {cadeia.elos.map((elo, i) => {
          const rota = elo.ausente ? null : rotaDaReferencia(elo.tipo, elo.id, elo.codigo)
          return (
            <Fragment key={elo.tipo}>
              {i > 0 && <Seta aria-hidden="true">›</Seta>}
              {rota
                ? <Link href={rota} aria-current={elo.atual ? 'step' : undefined} onClick={(e) => { e.preventDefault(); navigate(rota) }}><Conteudo elo={elo} /></Link>
                : <Conteudo elo={elo} marcar />}
            </Fragment>
          )
        })}
      </Faixa>
      {cadeia.depois && (
        <Depois>
          Depois disso: {cadeia.depois.movimento}
          {cadeia.depois.codigo && (
            <>
              {' · '}
              {cadeia.depois.rota
                ? <Ancora href={cadeia.depois.rota} onClick={(e) => { e.preventDefault(); navigate(cadeia.depois!.rota!) }}>{cadeia.depois.codigo}</Ancora>
                : cadeia.depois.codigo}
            </>
          )}
        </Depois>
      )}
    </Bloco>
  )
}

export default FaixaDaVida
